'use client';

import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { NavigationChevronDown } from '@energy-retailer/icons';
import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { ContentfulIcon } from '@/components/icon';
import { getPageUrl } from '@/lib/utils/get-page-url';

import { CustomLink } from '../../custom-link';

import type { Page, Link, Maybe } from '@/app/generated/graphql';
import { ANALYTICS_EVENTS } from '@/constants';

type Props = {
  title: string;
  link: Maybe<Link>;
  quickLinks?: Page[];
};
export const HeaderDropdown = ({ title, link, quickLinks }: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const pathname = usePathname();

  const dropDownClasses = isHovered
    ? 'opacity-100'
    : 'opacity-0 overflow-hidden h-0';

  const caretClassesRotate = isHovered
    ? 'transition-transform duration-0 -rotate-180 ease-in-out text-pink-500'
    : 'transition-transform duration-100 rotate-0 ease-in-out';

  return (
    <li
      className="relative items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      onClick={() => setIsHovered(true)}
    >
      <div className="text-body-3 flex items-center rounded-rad-a px-b font-brand font-semibold !text-gray-700 hover:!bg-gray-50 hover:!text-pink-500 hover:!no-underline focus-visible:ring-2 focus-visible:ring-pink-500 xl:px-a">
        <a
          className="!text-inherit py-a !text-gray-700 !no-underline hover:!text-pink-500"
          href={`/${link?.internalLink?.slug ?? ''}`}
        >
          {title}
        </a>
        <div className="flex items-center">
          <div className="-mt-0.5 pl-1">
            <NavigationChevronDown
              className={`border-1 h-5 w-5 border-blue-500 text-gray-700 ${caretClassesRotate}`}
            />
          </div>
        </div>
      </div>
      {/**
       * Padding between label and dropdown to enable the dropdown panel to stay open
       * when the mouse moves between the label and dropdown
       */}
      <div className="h-2 w-auto" />
      <ul className={`absolute left-0 z-20 w-[22rem]`}>
        <div
          className={`rounded-lg bg-white drop-shadow-lg transition-opacity duration-100 ${dropDownClasses}`}
        >
          {quickLinks?.map((page, index) => (
            <li
              key={page.slug}
              onBlur={() => {
                // Close the dropdown when tabbing out of the last link
                if (index === quickLinks.length - 1) {
                  setIsHovered(false);
                }
              }}
              className="last:rounded-b-lg hover:bg-gray-50"
            >
              <CustomLink
                href={getPageUrl(page)}
                confirmationRequired
                onClick={() => {
                  trackAnalyticsEvent(
                    ANALYTICS_EVENTS.headerDropdown.selected,
                    {
                      currentLocation: pathname,
                      destination: getPageUrl(page),
                      menuCategory: title,
                    }
                  );

                  setIsHovered(false);
                }}
                className="button flex w-full items-center px-e py-d text-sm sm:px-d sm:py-c lg:px-c lg:py-b"
              >
                {page.icon?.id ? (
                  <ContentfulIcon
                    className="mr-a h-icon w-icon text-pink-500"
                    id={page.icon.id}
                  />
                ) : null}
                {page.title}
              </CustomLink>
            </li>
          ))}
        </div>
      </ul>
    </li>
  );
};
